/**
 * A number as its cell's format writes it — the half of "looks like the export" that colours
 * cannot carry.
 *
 * ExcelJS reads the format string but does not apply it: a cell holding 1234.5 under `$#,##0.00`
 * comes back as "1234.5", so a cap table previewed without this reads as raw figures where the
 * file shows money, percentages and thousands separators.
 *
 * It covers the patterns this product's own exports write (`toExcelNumFmt`) and the common shapes
 * around them — a currency prefix, thousands grouping, fixed decimals, a percentage, and Excel's
 * positive;negative;zero triple. Anything else returns null and the caller falls back to the
 * library's plain text, which is the honest answer for a format we would otherwise guess at.
 */

const GROUPED = /#,#/;
const DECIMALS = /\.(0+)/;

// Excel writes one format for positives, then optionally negatives and zero, separated by `;`.
const sectionFor = (format: string, value: number): string => {
  const sections = format.split(';');

  if (value > 0 || sections.length === 1) return sections[0];
  if (value < 0) return sections[1] ?? sections[0];

  return sections[2] ?? sections[0];
};

const QUOTED = /"([^"]*)"/g;

// Stand-ins for the two things lifted out of the pattern before the number is placed in it.
const TEXT = '\u0000';
const DIGITS = '\u0001';

export const formatCellNumber = (
  value: number,
  format: null | string | undefined,
): null | string => {
  if (!format || format === 'General') return null;

  const section = sectionFor(format, value);
  // A section may be nothing but a literal — `"-"` for zero is how the exports write a dash.
  const literal = section.trim().match(/^"([^"]*)"$/);

  if (literal) return literal[1];

  const hasDigits = /[#0]/.test(section);

  if (!hasDigits) return null;

  // Anything the pattern quotes is text to keep; `[$-409]` and friends are locale hints to drop.
  const quoted: string[] = [];
  const skeleton = section
    .replace(QUOTED, (_match, text: string) => {
      quoted.push(text);

      return TEXT;
    })
    .replace(/\[[^\]]*\]/g, '');

  const isPercent = skeleton.includes('%');
  const decimals = skeleton.match(DECIMALS)?.[1].length ?? 0;
  const scaled = isPercent ? value * 100 : value;
  // The sign belongs to the pattern — `-0.00%` prints its own, and `+0.00%` demands one.
  const magnitude = Math.abs(scaled).toLocaleString('en-US', {
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals,
    useGrouping: GROUPED.test(skeleton),
  });

  // Split on the placeholders rather than matching them: a control character is not something a
  // regular expression should be looking for, and the pieces are all this needs.
  const [beforeDigits = '', afterDigits = ''] = skeleton.replace(/[#0,.]+/, DIGITS).split(DIGITS);
  const restoreText = (part: string, offset: number): string =>
    part
      .split(TEXT)
      .map((piece, index) => (index === 0 ? piece : (quoted[offset + index - 1] ?? '') + piece))
      .join('');

  const prefix = restoreText(beforeDigits, 0);
  const suffix = restoreText(afterDigits, beforeDigits.split(TEXT).length - 1);
  const sign = scaled < 0 && !prefix.includes('-') ? '-' : '';

  return `${sign}${prefix}${magnitude}${suffix}`;
};
